import { stringify } from "query-string";
import axios from "axios";
import { buildFormData } from "../utils/formData";
import { getAccessToken } from "./token";

export const apiUrl =
  typeof process !== 'undefined' && process.env.NODE_ENV === "development"
    ? "http://127.0.0.1:3000/v1/admin"
    : "https://api.application.fit/v1/admin";

export const axiosInstance = axios.create({
  baseURL: apiUrl,
  timeout: 60000,
  headers: { Accept: "application/json" },
});

axiosInstance.interceptors.request.use((config) => {
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Response interceptor for handling 401 errors and token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip refresh for login and refresh-tokens endpoints to avoid infinite loops
    if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh-tokens')) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const authData = localStorage.getItem('auth');
        if (authData) {
          const auth = JSON.parse(authData);
          const refreshToken = auth.tokens?.refresh?.token;

          if (refreshToken) {
            // Create a new axios instance without interceptors for refresh request
            const refreshAxios = axios.create({
              baseURL: apiUrl,
              timeout: 60000,
            });

            // Try to refresh the token
            const response = await refreshAxios.post('/auth/refresh-tokens', {
              refreshToken,
            });

            // Update tokens in localStorage
            auth.tokens = response.data;
            localStorage.setItem('auth', JSON.stringify(auth));

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${response.data.access.token}`;
            return axiosInstance(originalRequest);
          }
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('auth');
        if (window.location.pathname !== '/admin/login') {
          window.location.href = '/admin/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // If refresh failed or no refresh token, redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem('auth');
      if (window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }

    return Promise.reject(error);
  }
);

const baseDataProvider = {
  getList: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const stringifiedFilter = stringify(params.filter);

    const query = {
      sortBy: `${field}:${order.toLowerCase()}`,
      page,
      limit: perPage,
    };
    const url = `/${resource}?${stringify(query)}${
      stringifiedFilter ? `&${stringifiedFilter}` : ""
    }`;
    return axiosInstance.get(url).then((response) => {
      return {
        data: response.data.results,
        total: response.data.totalResults,
      };
    });
  },

  getOne: (resource, params) =>
    axiosInstance
      .get(`/${resource}/${params.id}`)
      .then((response) => ({
        data: response.data,
      })),

  getMany: async (resource, params, options) => {
    const query = {
      ...params,
      limit: 100,
    };
    const url = `/${resource}?${stringify(query)}`;
    return axiosInstance.get(url).then((response) => ({
      data: response.data.results,
    }));
  },

  getManyReference: async (resource, params) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const query = {
      sortBy: `${field}:${order.toLowerCase()}`,
      page,
      limit: perPage,
    };
    const url = `/${resource}?${stringify(query)}`;

    return axiosInstance.get(url).then((response) => ({
      data: response.data.results,
      total: response.data.totalResults,
    }));
  },

  update: async (resource, params) => {
    const hasFile =
      Object.values(params.data).findIndex(
        (param) => param?.rawFile
      ) !== -1;
    const formData = new FormData();
    buildFormData(formData, params.data);
    return axiosInstance
      .patch(
        `/${resource}/${params.id}`,
        hasFile ? formData : params.data
      )
      .then((response) => {
        return {
          data: response.data,
        };
      });
  },
  updateMany: async (resource, params) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    return axiosInstance
      .put(`/${resource}?${stringify(query)}`, {
        data: JSON.stringify(params.data),
      })
      .then((response) => ({ data: response.data }));
  },

  create: async (resource, params) => {
    const hasFile =
      Object.values(params.data).findIndex(
        (param) => param?.rawFile
      ) !== -1;
    const formData = new FormData();
    buildFormData(formData, params.data);
    return axiosInstance
      .post(
        `/${resource}`,
        hasFile ? formData : params.data
      )
      .then((response) => {
        return {
          data: { ...params.data, id: response.data.id },
        };
      });
  },

  delete: (resource, params) =>
    axiosInstance
      .delete(`/${resource}/${params.id}`)
      .then((response) => ({
        data: response.data,
      })),

  deleteMany: async (resource, params) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    return axiosInstance
      .delete(`/${resource}?${stringify(query)}`, {
        data: JSON.stringify(params.data),
      })
      .then((response) => ({ data: response.data }));
  },
  sendMessageToConversation: async (body) => {
    return axiosInstance
      .post(`/assistant/conversations/send`, body)
      .then((response) => response.data);
  },
  startJob: async (body) => {
    return axiosInstance
      .post(`/jobs/start`, body)
      .then((response) => response.data);
  },
  generateResponseConversation: async (body) => {
    return axiosInstance
      .post(
        `/assistant/conversations/generateResponse/${body.id}`
      )
      .then((response) => response.data);
  },
};

// const dataProvider = addRefreshAuthToAuthProvider

export default baseDataProvider;
